import { apiClient, ApiResponse } from '@packages/api-client';

// 版本数据接口
export interface OntologyVersion {
  id: number;
  versionName: string;
  versionNumber: number;
  versionDescription?: string;
  status: 'PUBLISHED' | 'DRAFT' | 'ARCHIVED';
  createdTime: string;
  createdBy: number;
  updateUser: string;
  updatedTime: string;
}

// 版本详情接口（包含图谱数据）
export interface OntologyVersionDetail extends OntologyVersion {
  graph?: {
    nodes?: any[];
    edges?: any[];
  };
}



// 版本API类
export class VersionApi {
  /**
   * 删除本体版本
   * @param versionId 版本ID
   * @returns 删除结果
   */
  static async deleteOntologyVersion(versionId: number): Promise<void> {
    try {
      console.log(`🚀 删除本体版本: ${versionId}`);
      
      await apiClient.delete(`/ontology-version/${versionId}`);
      
      console.log('✅ 删除本体版本成功');
    } catch (error: any) {
      console.error('❌ 删除本体版本失败:', error);
      throw error;
    }
  }

  /**
   * 创建本体版本
   * @param ontologyId 本体ID
   * @param versionData 版本数据
   * @returns 创建的版本信息
   */
  static async createOntologyVersion(ontologyId: number, versionData: Partial<OntologyVersion>): Promise<OntologyVersion> {
    try {
      console.log(`🚀 创建本体版本: ${ontologyId}`, versionData);
      
      const response = await apiClient.post<OntologyVersion>(`/ontology-version`, {
        ontologyId,
        ...versionData
      });
      
      console.log('✅ 创建本体版本成功:', response);
      
      return response.data;
    } catch (error: any) {
      console.error('❌ 创建本体版本失败:', error);
      throw error;
    }
  }

  /**
   * 更新本体版本
   * @param versionId 版本ID
   * @param versionData 版本数据
   * @returns 更新的版本信息
   */
  static async updateOntologyVersion(versionId: number, versionData: Partial<OntologyVersion>): Promise<OntologyVersion> {
    try {
      console.log(`🚀 更新本体版本: ${versionId}`, versionData);
      
      const response = await apiClient.put<OntologyVersion>(`/ontology-version/${versionId}`, versionData);
      
      console.log('✅ 更新本体版本成功:', response);
      
      return response.data;
    } catch (error: any) {
      console.error('❌ 更新本体版本失败:', error);
      throw error;
    }
  }

  /**
   * 获取版本详情（包含图谱数据）
   * @param versionId 版本ID
   * @returns 版本详情
   */
  static async getOntologyVersionDetail(versionId: number): Promise<OntologyVersionDetail> {
    try {
      console.log(`🚀 获取版本详情（包含图谱）: ${versionId}`);
      
      const response = await apiClient.get<OntologyVersionDetail>(`/ontology-version/${versionId}`);
      
      console.log('✅ 获取版本详情成功:', response);
      
      return response.data;
    } catch (error: any) {
      console.error('❌ 获取版本详情失败:', error);
      throw error;
    }
  }

  /**
   * 获取版本详情
   * @param versionId 版本ID
   * @returns 版本详情
   */
  static async getOntologyVersion(versionId: number): Promise<OntologyVersion> {
    try {
      console.log(`🚀 获取版本详情: ${versionId}`);
      
      const response = await apiClient.get<OntologyVersion>(`/ontology-version/${versionId}`);
      
      console.log('✅ 获取版本详情成功:', response);
      
      return response.data;
    } catch (error: any) {
      console.error('❌ 获取版本详情失败:', error);
      throw error;
    }
  }

  /**
   * 获取版本OPL文本
   * @param versionId 版本ID
   * @returns 版本OPL文本
   */
  static async getOntologyVersionOpl(versionId: number): Promise<string> {
    try {
      console.log(`🚀 获取版本OPL文本: ${versionId}`);
      
      const response = await apiClient.get<string>(`/ontology-version/${versionId}/opl`);
      
      console.log('✅ 获取版本OPL文本成功:', response);
      
      return response.data; // 返回实际的OPL文本数据
    } catch (error: any) {
      console.error('❌ 获取版本OPL文本失败:', error);
      throw error;
    }
  }

  /**
   * 发布版本
   * @param versionId 版本ID
   * @returns 发布结果
   */
  static async publishOntologyVersion(versionId: number): Promise<void> {
    try {
      console.log(`🚀 发布版本: ${versionId}`);
      
      await apiClient.post(`/ontology-version/${versionId}/publish`);
      
      console.log('✅ 发布版本成功');
    } catch (error: any) {
      console.error('❌ 发布版本失败:', error);
      throw error;
    }
  }

  /**
   * 重新发布版本
   * @param versionId 版本ID
   * @returns 重新发布结果
   */
  static async republishOntologyVersion(versionId: number): Promise<void> {
    try {
      console.log(`🚀 重新发布版本: ${versionId}`);
      
      await apiClient.put(`/ontology-version/${versionId}/republish`);
      
      console.log('✅ 重新发布版本成功');
    } catch (error: any) {
      console.error('❌ 重新发布版本失败:', error);
      throw error;
    }
  }

  /**
   * 归档版本
   * @param versionId 版本ID
   * @returns 归档结果
   */
  static async archiveOntologyVersion(versionId: number): Promise<void> {
    try {
      console.log(`🚀 归档版本: ${versionId}`);
      
      await apiClient.post(`/ontology-version/${versionId}/archive`);
      
      console.log('✅ 归档版本成功');
    } catch (error: any) {
      console.error('❌ 归档版本失败:', error);
      throw error;
    }
  }
  
  /** 新增版本接口 */

  /**
   * 下线版本
   * @param versionId 版本ID
   * @returns 下线结果
   */
  static async unpublishOntologyVersion(versionId: number): Promise<void> {
    try {
      console.log(`🚀 下线版本: ${versionId}`);
      
      await apiClient.put(`/ontology-version/${versionId}/unpublish`);
      
      console.log('✅ 下线版本成功');
    } catch (error: any) {
      console.error('❌ 下线版本失败:', error);
      throw error;
    }
  }
  
  /**
   * 编辑版本
   * @param versionId 版本ID
   * @param versionData 版本数据
   * @returns 编辑结果
   */
  static async editOntologyVersion(versionId: number, versionData: Partial<OntologyVersion>): Promise<void> {
    try {
      console.log(`🚀 编辑版本: ${versionId}`, versionData);
      
      await apiClient.put(`/ontology-version/${versionId}/edit`);
      
      console.log('✅ 编辑版本成功');
    } catch (error: any) {
      console.error('❌ 编辑版本失败:', error);
      throw error;
    }
  }
}